import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Divider } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { getPricing } from '@app/api/nominalPayments.api';
import ImportItemForm from './ImportItemsForm';
import ImportItemTable from './ImportItemsTable';

interface ImportItemsProps {
  onItemsChange?: (items: { products: any[]; service_charges: any[] }) => void;
  departmentOptions: { label: string; value: string }[];
  importItemsData?: {
    products: any[];
    service_charges: any[];
  };
  editable?: boolean;
  type?: 'edit' | 'create' | 'view';
}

const ImportItems: React.FC<ImportItemsProps> = ({
  onItemsChange,
  departmentOptions,
  importItemsData,
  editable,
  type,
}) => {
  const [items, setItems] = useState<any[]>([]);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const hasManuallyAddedItems = useRef(false);

  const calculateTotal = (item: any) => {
    return (item.qty || 1) * (item.unit_price || 0);
  };

  // Transform existing data to match the expected format (only for fetched data)
  const transformExistingData = (importItemsData: any) => {
    const transformedItems: any[] = [];

    const transformItem = (item: any, isDepartment6 = false) => {
      // Only transform if item has the nested structure (from API)
      if (item.department && item.product_category) {
        const baseItem = {
          key: uuidv4(),
          department_id: item.department_id,
          departmentLabel: `${item.department.code} - ${item.department.name}`,
          product_category_id: item.product_category_id,
          product_category: `${item.product_category.code} - ${item.product_category.name}`,
          product_variety_id: item.product_variety_id,
          product_variety: `${item.product_variety?.code} - ${item.product_variety?.name}`,
          product_id: item.product_id,
          product: item.product ? `${item.product.code} - ${item.product.name}` : '-',

          qty: parseFloat(item.qty),
          uom: item.uom || '-',
          unit_price: parseFloat(item.unit_price || 0),
          type: 'IMPORT',
          lcl: parseFloat(item.lcl) || 0,
          fcl: parseFloat(item.fcl) || 0,
          service_charge_id: item.service_charge_id,
          serviceCharge: item.service_charge ? `${item.service_charge.code} - ${item.service_charge.name}` : '-',
          serviceChargePrice: item.service_charge_price || 0,
          // Keep original item data for reference if needed
          originalItem: item,
          // Use the API's total for existing items, fall back to calculation for new items
          total: parseFloat(item.total || '0'),
        };

        // Add product variety if exists
        if (item.product_variety) {
          baseItem.product_variety_id = item.product_variety_id;
          baseItem.product_variety = `${item.product_variety?.code} - ${item.product_variety?.name}`;
        } else {
          baseItem.product_variety_id = null;
          baseItem.product_variety = '-';
        }

        // Add service-specific fields for department 6
        if (isDepartment6) {
          baseItem.lcl = parseFloat(item.lcl) || 0;
          baseItem.fcl = parseFloat(item.fcl) || 0;
          baseItem.service_charge_id = item.service_charge_id || null;

          if (item.service_charge) {
            baseItem.serviceCharge = `${item.service_charge.code} - ${item.service_charge.name}`;
            baseItem.serviceChargePrice = item.service_charge_price || 0;
          } else {
            baseItem.serviceCharge = '-';
            baseItem.serviceChargePrice = 0;
          }
        }

        // Only calculate total if API doesn't provide one
        if (!item.total || parseFloat(item.total) === 0) {
          baseItem.total = calculateTotal(baseItem);
        }

        return baseItem;
      }
      return null; // Skip items that don't have the expected structure
    };

    // Transform products
    if (importItemsData?.products) {
      importItemsData.products.forEach((product: any) => {
        const transformedItem = transformItem(product, product.department_id === 6);
        if (transformedItem) {
          transformedItems.push(transformedItem);
        }
      });
    }

    // Transform services
    if (importItemsData?.service_charges) {
      importItemsData.service_charges.forEach((service: any) => {
        const transformedItem = transformItem(service, true);
        if (transformedItem) {
          transformedItems.push(transformedItem);
        }
      });
    }

    return transformedItems;
  };

  // Helper function to separate items and notify parent
  const groupItemsByType = useCallback((items: any[]) => {
    const products = items.filter((item) => item.department_id !== 6);
    const service_charges = items.filter((item) => item.department_id === 6);

    return { products, service_charges };
  }, []);

  useEffect(() => {
    // Only run this effect once when importItemsData is first provided
    // and only if we haven't loaded initial data yet and haven't manually added items
    if (!initialDataLoaded && !hasManuallyAddedItems.current) {
      if (importItemsData && (importItemsData.products?.length || importItemsData.service_charges?.length)) {
        console.log('Loading initial import data...');
        const transformedItems = transformExistingData(importItemsData);
        console.log('Transformed import items:', transformedItems);
        setItems(transformedItems);
        // Also notify parent about the initial data
        onItemsChange?.(groupItemsByType(transformedItems));
      }
      setInitialDataLoaded(true);
    }
  }, [importItemsData]);

  const handleAddItem = (item: any) => {
    hasManuallyAddedItems.current = true;
    const newItem = {
      ...item,
      key: uuidv4(),
      type: 'IMPORT',
      unit_price: item.unit_price || 0,
      serviceChargePrice: item.serviceChargePrice || 0,
    };

    // Calculate total after ensuring prices are set
    newItem.total = calculateTotal(newItem);

    console.log('New item before adding:', newItem);
    console.log('New item total:', newItem.total);

    setItems((prevItems) => {
      const updatedItems = [...prevItems, newItem];
      console.log('Updated import items:', updatedItems);
      setTimeout(() => {
        onItemsChange?.(groupItemsByType(updatedItems));
      }, 0);
      return updatedItems;
    });
  };

  const handleRemoveItem = (key: string) => {
    console.log('Removing import item:', key);
    setItems((prevItems) => {
      const updatedItems = prevItems.filter((item) => item.key !== key);
      setTimeout(() => {
        onItemsChange?.(groupItemsByType(updatedItems));
      }, 0);
      return updatedItems;
    });
  };

  const handleQtyChange = async (key: string, newQty: number) => {
    console.log('handleQtyChange called:', { key, newQty });

    try {
      // Find the item to update
      const itemIndex = items.findIndex((item) => item.key === key);
      if (itemIndex === -1) {
        console.error('Item not found:', key);
        throw new Error('Item not found');
      }

      const item = items[itemIndex];
      console.log('Found item:', item);

      if (item.department_id === 6) {
        console.log('Updating harbour service item directly');

        const updatedItem = {
          ...item,
          qty: newQty,
        };
        updatedItem.total = calculateTotal(updatedItem);

        setItems((prevItems) => {
          const updatedItems = [...prevItems];
          updatedItems[itemIndex] = updatedItem;
          // Notify parent about the changes
          setTimeout(() => {
            onItemsChange?.(groupItemsByType(updatedItems));
          }, 0);
          return updatedItems;
        });
        return;
      }

      const pricingModel =
        item.department_id === 6 ? 'SERVICE_CHARGE' : item.department_id === 7 ? 'PRODUCT' : 'PRODUCT';

      const pricingResponse = await getPricing(
        'IMPORT',
        item.product_category_id,
        item.product_id,
        item.product_variety_id || null,
        item.department_id,
        0,
        newQty,
        pricingModel,
        null, // lab_service_id
        item.service_charge_id || null,
      );

      console.log('API response:', pricingResponse);

      const pricingData = pricingResponse?.data;

      if (!pricingData) {
        throw new Error('No pricing data received');
      }

      // Update the item with new values
      setItems((prevItems) => {
        const updatedItems = [...prevItems];
        const updatedItem = {
          ...item,
          qty: newQty,
          uom: pricingData.uom || item.uom,
          unit_price: Number(pricingData.unit_price || '0'),
        };

        updatedItem.total = calculateTotal(updatedItem);

        updatedItems[itemIndex] = updatedItem;

        console.log('Updated item:', updatedItems[itemIndex]);

        // Notify parent about the changes
        setTimeout(() => {
          onItemsChange?.(groupItemsByType(updatedItems));
        }, 0);

        return updatedItems;
      });
    } catch (error) {
      console.error('Error in handleQtyChange:', error);
      throw error;
    }
  };

  const handleFieldChange = (key: string, field: 'lcl' | 'fcl', value: number) => {
    setItems((prevItems) => {
      const updatedItems = prevItems.map((item) => {
        if (item.key === key) {
          return {
            ...item,
            [field]: value,
            total: (item.unit_price || 0) * (field === 'lcl' || field === 'fcl' ? item.qty || 1 : 1),
          };
        }
        return item;
      });

      // Notify parent if needed
      setTimeout(() => {
        onItemsChange?.(groupItemsByType(updatedItems));
      }, 0);

      return updatedItems;
    });
  };

  return (
    <>
      {editable && (
        <>
          <Card title="Add Import Items" size="small">
            <ImportItemForm onAdd={handleAddItem} departmentOptions={departmentOptions} />
          </Card>
          <Divider />
        </>
      )}

      <ImportItemTable
        data={items}
        onRemove={editable ? handleRemoveItem : undefined}
        editable={editable}
        type={type}
        onQtyChange={editable && type !== 'view' ? handleQtyChange : undefined}
        handleFieldChange={editable && type !== 'view' ? handleFieldChange : undefined}
      />
    </>
  );
};

export default ImportItems;
